import deepmerge from 'deepmerge'; // < 1kb payload overhead when lodash/merge is > 3kb.
import {isPlainObject} from 'is-plain-object';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import createMixins from '@material-ui/core/styles/createMixins';
import createPalette from '@material-ui/core/styles/createPalette';
import createTypography from '@material-ui/core/styles/createTypography';
import shadows from '@material-ui/core/styles/shadows';
import shape from '@material-ui/core/styles/shape';
import createSpacing from '@material-ui/core/styles/createSpacing';
import transitions from '@material-ui/core/styles/transitions';
import zIndex from '@material-ui/core/styles/zIndex';

function createMuiTheme(options = {}) {
    const {
        breakpoints: breakpointsInput = {},
        mixins: mixinsInput = {},
        palette: paletteInput = {},
        shadows: shadowsInput,
        spacing: spacingInput,
        typography: typographyInput = {},
        ...other
    } = options;

    const palette = createPalette(paletteInput);
    const breakpoints = createBreakpoints(breakpointsInput);
    const spacing = createSpacing(spacingInput);

    const muiTheme = {
        breakpoints,
        direction: 'ltr',
        mixins: createMixins(breakpoints, spacing, mixinsInput),
        overrides: {}, // Inject custom styles
        palette,
        props: {}, // Inject custom props
        shadows: shadowsInput || shadows,
        typography: createTypography(palette, typographyInput),
        spacing,
        ...deepmerge(
            {
                shape,
                transitions,
                zIndex,
            },
            other,
            {
                isMergeableObject: isPlainObject,
            },
        ),
    };

    if (process.env.NODE_ENV !== 'production') {
        const pseudoClasses = [
            'checked',
            'disabled',
            'error',
            'focused',
            'focusVisible',
            'required',
            'expanded',
            'selected',
        ];
        const traverse = (node, parentKey, depth = 1) => {
            let key;

            // eslint-disable-next-line guard-for-in, no-restricted-syntax
            for (key in node) {
                const child = node[key];
                if (depth === 1) {
                    if (key.indexOf('Mui') === 0 && child) {
                        traverse(child, key, depth + 1);
                    }
                } else if (pseudoClasses.indexOf(key) !== -1 && Object.keys(child).length > 0) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error(
                            [
                                `Material-UI: the \`${parentKey}\` component increases ` +
                                `the CSS specificity of the \`${key}\` internal state.`,
                                'You can not override it like this: ',
                                JSON.stringify(node, null, 2),
                                '',
                                'Instead, you need to use the $ruleName syntax:',
                                JSON.stringify(
                                    {
                                        root: {
                                            [`&$${key}`]: child,
                                        },
                                    },
                                    null,
                                    2,
                                ),
                                '',
                                'https://material-ui.com/r/pseudo-classes-guide',
                            ].join('\n'),
                        );
                    }
                    // Remove the style to prevent global conflicts.
                    node[key] = {};
                }
            }
        };

        traverse(muiTheme.overrides);
    }

    if (process.env.NODE_ENV !== 'production') {
        if (muiTheme.shadows.length !== 25) {
            console.error(
                'Material-UI: the shadows array provided to createMuiTheme should support 25 elevations.',
            );
        }
    }

    return muiTheme;
}

export default createMuiTheme;